import { useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useOrganization } from "../context/OrganizationContext";
import {
  groupVisitor,
  identifyVisitor,
  initVisitorTracking,
  isVisitorTrackingEnabled,
  resetVisitor,
} from "../utils/snitcher";

/**
 * Knoopt de ingelogde gebruiker en zijn organisatie aan de Snitcher-sessie.
 *
 * Waarom dit bestaat: anonieme bedrijfsherkenning op IP is een schatting.
 * Zodra iemand hier inlogt is de identiteit zeker, en pas dan is bezoekdata
 * bruikbaar voor sales — "Van Werven keek naar de ERE-calculator" in plaats
 * van "een bedrijf in Oldebroek, waarschijnlijk".
 *
 * Partneraccounts worden bewust overgeslagen. Servicepartners zijn geen
 * klanten en geen leads; ze in de bedrijfsherkenning meenemen vervuilt de
 * segmenten waar commercie op stuurt.
 */
export function useVisitorIdentification(): void {
  const { user, isAuthenticated } = useAuth();
  const { currentOrganization } = useOrganization();

  // Voorkomt dat elke re-render opnieuw identificeert. `null` betekent:
  // er is op dit moment niets gekoppeld.
  const lastIdentityRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isVisitorTrackingEnabled()) return;

    const isTrackableUser = isAuthenticated && user !== null && user.partnerId === null;

    if (!isTrackableUser) {
      // Alleen resetten als er iets te resetten valt — anders sturen we bij
      // elke pagina-load van een uitgelogde bezoeker een lege reset.
      if (lastIdentityRef.current !== null) {
        resetVisitor();
        lastIdentityRef.current = null;
      }
      return;
    }

    const identity = `${user.id}:${currentOrganization?.id ?? ""}`;
    if (identity === lastIdentityRef.current) return;

    initVisitorTracking();
    identifyVisitor(user.id);

    if (currentOrganization !== null) {
      groupVisitor(currentOrganization.id, {
        name: currentOrganization.name,
        kvkNumber: currentOrganization.kvkNumber,
        organizationType: currentOrganization.organizationType,
      });
    }

    lastIdentityRef.current = identity;
  }, [isAuthenticated, user, currentOrganization]);
}
